import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { parse } from 'query-string'

import { getCurrentBusiness } from '../../../store/duck/businesses'
import { getCurrentClient } from '../../../store/duck/clients'
import {
  getMembershipIsPortalEmbed,
  getMembershipIsPosFlow,
  getMembershipSelection,
} from '../../../store/duck/wellnessPlans'
import { KIOSK_MEMBERSHIP_ENROLLMENT_FLOW_ID_QUERY_PARAM } from '../../../utils/membershipQueryParams'
import { trackEvent } from '../../../utils/trackingProvider'
import {
  type KioskMembershipEnrollmentAnalyticsKey,
  type KioskMembershipEnrollmentTrackingOverrides,
  buildKioskMembershipEnrollmentTrackingData,
  KIOSK_MEMBERSHIP_ENROLLMENT_TRACKING_EVENT,
} from './membershipEnrollmentAnalytics'

const STORAGE_KEY_PREFIX = 'kiosk-membership-enrollment'
let kioskMembershipEnrollmentFlowIdCounter = 0

const getStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage
  } catch (error) {
    return null
  }
}

const getScalarValue = (value: string | (string | null)[] | null | undefined) =>
  Array.isArray(value) ? value[0] : value

const createFlowId = (): string =>
  globalThis.crypto?.randomUUID?.() ||
  (() => {
    kioskMembershipEnrollmentFlowIdCounter += 1

    return `kiosk-membership-enrollment-${Date.now()}-${kioskMembershipEnrollmentFlowIdCounter}`
  })()

const getInteractionOrderStorageKey = (flowId: string) =>
  `${STORAGE_KEY_PREFIX}:interaction-order:${flowId}`

const getStartedAtStorageKey = (flowId: string) =>
  `${STORAGE_KEY_PREFIX}:started-at:${flowId}`

const readStoredNumber = (key: string, fallback = 0): number => {
  const storage = getStorage()

  if (!storage) {
    return fallback
  }

  const value = Number(storage.getItem(key))

  return Number.isFinite(value) ? value : fallback
}

const writeStoredNumber = (key: string, value: number): void => {
  const storage = getStorage()

  if (!storage) {
    return
  }

  storage.setItem(key, String(value))
}

const initializeFlowTrackingContext = (flowId: string): void => {
  if (!flowId) {
    return
  }

  const storage = getStorage()

  if (!storage) {
    return
  }

  const startedAtStorageKey = getStartedAtStorageKey(flowId)
  const interactionOrderStorageKey = getInteractionOrderStorageKey(flowId)

  if (!storage.getItem(startedAtStorageKey)) {
    storage.setItem(startedAtStorageKey, String(Date.now()))
  }

  if (!storage.getItem(interactionOrderStorageKey)) {
    storage.setItem(interactionOrderStorageKey, '0')
  }
}

const getCurrentInteractionOrderForFlow = (flowId: string): number =>
  flowId ? readStoredNumber(getInteractionOrderStorageKey(flowId), 0) : 0

const incrementInteractionOrderForFlow = (flowId: string): number => {
  if (!flowId) {
    return 0
  }

  const nextInteractionOrder = getCurrentInteractionOrderForFlow(flowId) + 1
  writeStoredNumber(getInteractionOrderStorageKey(flowId), nextInteractionOrder)

  return nextInteractionOrder
}

const getFlowStartedAtForFlow = (flowId: string): number | null =>
  flowId ? readStoredNumber(getStartedAtStorageKey(flowId), Date.now()) : null

export const getKioskMembershipEnrollmentInteractionOrder = (
  flowId?: string,
): number => (flowId ? getCurrentInteractionOrderForFlow(flowId) : 0)

export const useKioskMembershipEnrollmentTracking = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentBusiness = useSelector(getCurrentBusiness)
  const currentClient = useSelector(getCurrentClient)
  const isPortalEmbed = useSelector(getMembershipIsPortalEmbed)
  const isPosMembershipFlow = useSelector(getMembershipIsPosFlow)
  const selection = useSelector(getMembershipSelection)
  const fallbackFlowIdRef = useRef<string | null>(null)

  const { flowId, queryFlowId } = useMemo(() => {
    const query = parse(location.search)
    const queryMembershipFlowId = getScalarValue(
      query[KIOSK_MEMBERSHIP_ENROLLMENT_FLOW_ID_QUERY_PARAM],
    )

    return {
      flowId:
        queryMembershipFlowId ||
        fallbackFlowIdRef.current ||
        (() => {
          const fallbackFlowId = createFlowId()
          fallbackFlowIdRef.current = fallbackFlowId

          return fallbackFlowId
        })(),
      queryFlowId: queryMembershipFlowId,
    }
  }, [location.search])

  useEffect(() => {
    initializeFlowTrackingContext(flowId)
  }, [flowId])

  useEffect(() => {
    if (queryFlowId || !flowId || !location.pathname) {
      return
    }

    const nextSearchParams = new URLSearchParams(location.search)
    nextSearchParams.set(
      KIOSK_MEMBERSHIP_ENROLLMENT_FLOW_ID_QUERY_PARAM,
      flowId,
    )
    const nextSearch = nextSearchParams.toString()

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true, state: location.state },
    )
  }, [
    flowId,
    location.pathname,
    location.search,
    location.state,
    navigate,
    queryFlowId,
  ])

  const latestValuesRef = useRef({
    businessId: currentBusiness?.id,
    clientId: currentClient?.id,
    flowId,
    selectionLength: selection?.length || 0,
  })

  useEffect(() => {
    latestValuesRef.current = {
      businessId: currentBusiness?.id,
      clientId: currentClient?.id,
      flowId,
      selectionLength: selection?.length || 0,
    }
  }, [currentBusiness?.id, currentClient?.id, flowId, selection?.length])

  const getCurrentInteractionOrder = useCallback(
    () => getCurrentInteractionOrderForFlow(latestValuesRef.current.flowId),
    [],
  )

  const trackMembershipEnrollment = useCallback(
    (
      eventKey: KioskMembershipEnrollmentAnalyticsKey,
      overrides: KioskMembershipEnrollmentTrackingOverrides = {},
    ) => {
      const {
        businessId,
        clientId,
        flowId: latestFlowId,
        selectionLength,
      } = latestValuesRef.current

      if (!latestFlowId || !isPortalEmbed || !isPosMembershipFlow) {
        return
      }

      const interactionOrderId = incrementInteractionOrderForFlow(latestFlowId)

      trackEvent(
        KIOSK_MEMBERSHIP_ENROLLMENT_TRACKING_EVENT,
        buildKioskMembershipEnrollmentTrackingData({
          businessId: businessId ?? undefined,
          clientId,
          eventKey,
          flowId: latestFlowId,
          flowStartedAt: getFlowStartedAtForFlow(latestFlowId),
          interactionOrderId,
          overrides,
          selectedPatientCount: selectionLength,
        }),
      )
    },
    [isPortalEmbed, isPosMembershipFlow],
  )

  return {
    flowId,
    getCurrentInteractionOrder,
    trackMembershipEnrollment,
  }
}
